import React, { type ReactNode, useCallback, useState } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import SuccessIcon from '@atlaskit/icon/glyph/check-circle';
import ErrorIcon from '@atlaskit/icon/glyph/error';
import { G300, R300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { FormattedI18nMessage } from '@atlassian/jira-formatted-i18n-message/src/ui/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { FlagProps } from './toggle/types';
import type { UseToggleProps } from './types';

const Bold = ({ children }: { children: ReactNode }) => <b>{children}</b>;

export const useToggle = ({ messages }: UseToggleProps) => {
	const { formatMessage } = useIntl();
	const [flag, setFlag] = useState<FlagProps | null>(null);

	const handleDismiss = useCallback(() => setFlag(null), []);

	const onToggleError = useCallback(() => {
		const err = new Error('Unable to update issue search transition setting');
		fireErrorAnalytics({
			meta: {
				id: 'newIssueSearchTransitionToggle',
				packageName: 'jiraAdminPagesNewIssueSearchTransition',
				teamName: 'empanada',
			},
			sendToPrivacyUnsafeSplunk: true,
			error: err,
		});
		setFlag({
			id: 'admin-pages-new-issue-search-transition.ui.toggle-error',
			description: formatMessage(messages.toggleFailedDescription),
			title: formatMessage(messages.toggleFailedHeading),
			icon: <ErrorIcon label="error" primaryColor={token('color.icon.danger', R300)} />,
		});
	}, [formatMessage, messages.toggleFailedDescription, messages.toggleFailedHeading]);

	const onToggleSuccess = useCallback(
		(isToggled: boolean, analyticsEvent: UIAnalyticsEvent) => {
			fireUIAnalytics(analyticsEvent, 'newIssueSearchTransitionToggleState', {
				hasToggledOn: isToggled,
			});

			setFlag({
				id: 'admin-pages-new-issue-search-transition.ui.toggle-success',
				description: isToggled ? (
					formatMessage(messages.toggleSuccessOnDescription)
				) : (
					<FormattedI18nMessage
						componentsMapping={{ bold: Bold }}
						message={formatMessage(messages.toggleSuccessOffDescription, {
							boldStart: '{boldStart}',
							boldEnd: '{boldEnd}',
						})}
					/>
				),
				title: isToggled
					? formatMessage(messages.toggleSuccessOnHeading)
					: formatMessage(messages.toggleSuccessOffHeading),
				icon: <SuccessIcon label="" primaryColor={token('color.icon.success', G300)} />,
			});
		},
		[
			formatMessage,
			messages.toggleSuccessOffDescription,
			messages.toggleSuccessOffHeading,
			messages.toggleSuccessOnDescription,
			messages.toggleSuccessOnHeading,
		],
	);

	return [{ flag }, { handleDismiss, onToggleError, onToggleSuccess }] as const;
};
